import React from 'react';
import gql from 'graphql-tag';
import { MutationFn } from 'react-apollo';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { DismissUserUpdate, CoreHomepageNotifications } from '@reverbdotcom/commons/src/rql';
import { HOMEPAGE_NOTIFICATIONS_QUERY } from './homepage_notifications';

export type DismissUserUpdateMutationFn = MutationFn<DismissUserUpdate.Mutation, DismissUserUpdate.Variables>;

const mutationFnName = 'dismissUserUpdate';
type EmptyObject = Record<string, never>;

export const withDismissUserUpdateMutation =
  withGraphql<EmptyObject, DismissUserUpdate.Mutation, DismissUserUpdate.Variables>(
    gql`
      mutation DismissUserUpdate($input: Input_reverb_updates_DismissMyUpdateRequest) {
        dismissUserUpdate(input: $input) {
          dismissed
        }
      }
    `,
    {
      name: mutationFnName,
    },
  );

export interface DismissUserUpdateState {
  dismissUserUpdate: () => any;
}

export function useDismissUserUpdate(
  dismissUserUpdate: DismissUserUpdateMutationFn,
  updateId: string,
): DismissUserUpdateState {
  const [mutate] = useMutation(dismissUserUpdate);

  const dismissUserUpdateCallback = React.useCallback(async function () {
    return mutate({
      variables: {
        input: {
          updateId,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        dismissUserUpdate: {
          __typename: 'reverb_updates_DimissMyUpdateResponse',
          dismissed: true,
        },
      },
      update: function (cache, mutationResult) {
        const dismissed = mutationResult.data?.dismissUserUpdate?.dismissed;

        if (dismissed) {
          const data = cache.readQuery({ query: HOMEPAGE_NOTIFICATIONS_QUERY, variables: { skip: false } }) as CoreHomepageNotifications.Query;
          const dismissedIndex = data.me.homepageNotifications.homepageNotifications.findIndex(n => n.updateId === updateId);
          if (dismissedIndex >= 0) {
            data.me.homepageNotifications.homepageNotifications[dismissedIndex].dismissed = true;
          }
          cache.writeQuery({
            data,
            variables: { skip: false },
            query: HOMEPAGE_NOTIFICATIONS_QUERY,
          });
        }

        return true;
      },
    });
  }, [updateId, mutate]);

  return { dismissUserUpdate: dismissUserUpdateCallback };
}
